import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useUnreadNotifications, Notification } from "@/hooks/useUnreadNotifications";
import { formatDistanceToNow } from "date-fns";
import { ptBR, de } from "date-fns/locale";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { translateNotificationTitle, translateNotificationMessage } from "@/lib/notificationTranslations";

interface NotificationBellProps {
  variant?: "light" | "dark";
}

export function NotificationBell({ variant = "light" }: NotificationBellProps) {
  const { language } = useLanguage();
  const { userRole, userRoleData } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const wasOpenRef = useRef(false);
  const { unreadCount, notifications, isLoading, markAllAsRead } =
    useUnreadNotifications();

  const isCoAdmin = userRole === "employee" && userRoleData?.employee_type === "co_admin";

  const resolveActionUrl = (url: string) => {
    // Co-admins should stay in /employee/ routes, not /admin/
    if (isCoAdmin && url.startsWith("/admin/")) {
      return url.replace(/^\/admin\//, "/employee/");
    }
    return url;
  };

  // Auto mark all as read when popover closes
  useEffect(() => {
    if (wasOpenRef.current && !isOpen && unreadCount > 0) {
      markAllAsRead();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, unreadCount, markAllAsRead]);

  const handleNotificationClick = (notification: Notification) => {
    if (notification.action_url) {
      setIsOpen(false);
      navigate(resolveActionUrl(notification.action_url));
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "pipeline": return "🔄";
      case "sale": return "💰";
      case "content": return "📸";
      case "message": return "💬";
      case "calendar": return "📅";
      case "verification_failed": return "❌";
      default: return "🔔";
    }
  };

  const isDark = variant === "dark";

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "relative",
            isDark
              ? "text-background hover:bg-background/10"
              : "text-foreground hover:bg-muted"
          )}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[20px] h-[20px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 animate-pulse">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end" sideOffset={8}>
        <div className="flex items-center justify-between p-4 border-b">
          <h4 className="font-semibold text-sm">
            {language === "de" ? "Benachrichtigungen" : "Notificações"}
          </h4>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-7 px-2"
            onClick={() => {
              setIsOpen(false);
              navigate("/notificacoes");
            }}
          >
            <ExternalLink className="h-3 w-3 mr-1" />
            {language === "de" ? "Alle anzeigen" : "Ver todas"}
          </Button>
        </div>
        <ScrollArea className="h-[300px]">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <Bell className="h-8 w-8 mb-2 opacity-50" />
              <p className="text-sm">{language === "de" ? "Keine Benachrichtigungen" : "Nenhuma notificação"}</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={cn(
                    "p-3 cursor-pointer transition-colors hover:bg-muted/50",
                    !notification.is_read && "bg-primary/5"
                  )}
                >
                  <div className="flex gap-3">
                    <span className="text-lg flex-shrink-0">
                      {getNotificationIcon(notification.type)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={cn("text-sm font-medium truncate", !notification.is_read && "text-foreground")}>
                          {translateNotificationTitle(notification.title, language)}
                        </p>
                        {!notification.is_read && (
                          <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-1.5" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {translateNotificationMessage(notification.message, language)}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {formatDistanceToNow(new Date(notification.created_at), {
                          addSuffix: true,
                          locale: language === "de" ? de : ptBR,
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
